/* eslint-disable */

import type { Config } from "@wagmi/core"
import type { Address } from "viem"
import { useEffect, useState } from "react"
import { multicall } from "@wagmi/core"

import type { Token } from "@/domains/core"
import { minimalTokenAbi, minimalVoterAbi } from "@/domains/abis"
import { unixTimestamp } from "@/domains/library"
import { TokenStore } from "@/domains/tokens"

import type { ManageLockLensResult, MulticallContractConfig } from "../types"
import { BIGINT_ZERO } from "../constants"
import { makeWeiReturnObject } from "../make-wei-return-object"

type UseManageLockLensProps = {
  nftId?: number
  config: Config
  chainId: number
  account?: Address
  vestedToken: Token
  voterAddress: Address
  governanceToken: TokenStore
  refetchInterval?: number
}

export const useManageLockLens = ({
  nftId,
  config,
  chainId,
  account,
  vestedToken,
  voterAddress,
  governanceToken,
  refetchInterval = 5 * 1000,
}: UseManageLockLensProps): ManageLockLensResult => {
  const [result, setResult] = useState<ManageLockLensResult>({
    accountBalanceToken: { wei: BIGINT_ZERO, value: "0" },
    vestedSpendTokenAllowance: { wei: BIGINT_ZERO, value: "0" },
    hasVoted: false,
  })

  useEffect(() => {
    let isSubscribed = true

    const fetchData = async () => {
      if (!nftId || !account) return
      try {
        const contracts = [
          {
            address: governanceToken.address.toViemAddress(),
            abi: minimalTokenAbi,
            functionName: "balanceOf",
            args: [account],
            chainId,
          },
          {
            address: governanceToken.address.toViemAddress(),
            abi: minimalTokenAbi,
            functionName: "allowance",
            args: [account, vestedToken.address],
            chainId,
          },
          {
            address: voterAddress,
            abi: minimalVoterAbi,
            functionName: "lastVoted",
            args: [nftId],
            chainId,
          },
        ] satisfies MulticallContractConfig[]

        const lens = await multicall(config, { contracts })

        if (isSubscribed) {
          const lastVoted = Number(lens[2]?.result as bigint)
          const now = unixTimestamp()
          const hasVoted = lastVoted > 0 ? lastVoted > now - (now % (86400 * 7)) : false

          setResult({
            accountBalanceToken: makeWeiReturnObject(lens[0]?.result as bigint, governanceToken.decimals),
            vestedSpendTokenAllowance: makeWeiReturnObject(lens[1]?.result as bigint, governanceToken.decimals),
            hasVoted,
          })
        }
      } catch (error: any) {
        console.error("useManageLockLens", error)
      }
    }

    fetchData()

    const intervalId = setInterval(fetchData, refetchInterval)

    return () => {
      isSubscribed = false
      clearInterval(intervalId)
    }
  }, [
    chainId,
    config,
    governanceToken.address.toViemAddress,
    vestedToken.address,
    nftId,
    voterAddress,
    account,
    refetchInterval,
  ])

  return result
}
